<template>
  <div class="home-page" :class="{ 'is-english': isEnglish }" ref="homePage">
    <div class="home-motion-bg" aria-hidden="true">
      <span class="sound-wave wave-one">
        <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i>
      </span>
      <span class="sound-wave wave-two">
        <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i>
      </span>
      <span class="sound-wave wave-three">
        <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i>
      </span>
      <span class="caption-rail rail-one"></span>
      <span class="caption-rail rail-two"></span>
      <span class="caption-rail rail-three"></span>
    </div>

    <HomeHeroPreview
      :is-logged-in="isLoggedIn"
      @start="handleStart"
      @demo="handleDemo"
    />

    <HomeLibrarySection />

    <HomeWorkflowSection />

    <HomeScenarioSection
      :is-logged-in="isLoggedIn"
      @demo="handleDemo"
    />

    <section class="section stats-section">
      <div class="section-copy">
        <h2 class="section-title">{{ $t('home.statsTitle') }}</h2>
      </div>
      <div class="stats-grid">
        <div v-for="stat in stats" :key="stat.label" class="stat-item">
          <strong class="stat-value">{{ stat.value }}</strong>
          <span class="stat-label">{{ stat.label }}</span>
        </div>
      </div>
    </section>

    <section class="section pricing-section" id="pricing">
      <div class="section-copy">
        <h2 class="section-title">{{ $t('home.pricing.title') }}</h2>
      </div>
      <div
        class="pricing-info"
        role="button"
        tabindex="0"
        @click="handleShowPremium"
        @keydown.enter.prevent="handleShowPremium"
        @keydown.space.prevent="handleShowPremium"
      >
        <div class="plan-card free">
          <span class="plan-label">{{ $t('home.pricing.free.label') }}</span>
          <h3>{{ $t('home.pricing.free.title') }}</h3>
          <ul>
            <li>{{ $t('home.pricing.free.credits') }}</li>
            <li>{{ $t('home.pricing.free.pronunciation') }}</li>
            <li>{{ $t('home.pricing.free.transcription') }}</li>
            <li>{{ $t('home.pricing.free.tts') }}</li>
          </ul>
        </div>
        <div class="plan-card pro">
          <span class="plan-label">{{ $t('home.pricing.pro.label') }}</span>
          <h3>{{ $t('home.pricing.pro.title') }}</h3>
          <ul>
            <li>{{ $t('home.pricing.pro.credits') }}</li>
            <li>{{ $t('home.pricing.pro.pronunciation') }}</li>
            <li>{{ $t('home.pricing.pro.transcription') }}</li>
            <li>{{ $t('home.pricing.pro.tts') }}</li>
          </ul>
        </div>
        <div class="plan-card premium">
          <span class="plan-label">{{ $t('home.pricing.premium.label') }}</span>
          <h3>{{ $t('home.pricing.premium.title') }}</h3>
          <ul>
            <li>{{ $t('home.pricing.premium.credits') }}</li>
            <li>{{ $t('home.pricing.premium.pronunciation') }}</li>
            <li>{{ $t('home.pricing.premium.transcription') }}</li>
            <li>{{ $t('home.pricing.premium.tts') }}</li>
          </ul>
        </div>
      </div>
      <p class="pricing-allowance-note">{{ $t('home.pricing.allowanceNote') }}</p>
    </section>

    <section class="section faq-section" id="faq">
      <div class="split-layout">
        <div class="section-copy">
          <h2 class="section-title">{{ $t('home.faqTitle') }}</h2>
          <p>{{ $t('home.faqSubtitle') }}</p>
        </div>
        <div class="faq-container">
          <details class="faq-item" v-for="(item, index) in $tm('home.faq')" :key="index">
            <summary>
              <span>{{ item.question }}</span>
              <span class="material-icons faq-toggle" aria-hidden="true">add</span>
            </summary>
            <div class="faq-answer">
              <p>{{ item.answer }}</p>
            </div>
          </details>
        </div>
      </div>
      <div class="partner-band">
        <div class="partner-band-copy">
          <strong>{{ $t('home.partnerBand.title') }}</strong>
          <span>{{ $t('home.partnerBand.desc') }}</span>
        </div>
        <router-link to="/partner" class="partner-band-link">
          {{ $t('home.partnerBand.action') }}
          <span class="material-icons" aria-hidden="true">arrow_forward</span>
        </router-link>
      </div>
    </section>

    <HomeFooter
      :is-logged-in="isLoggedIn"
      :footer-tools="footerTools"
      @start="handleStart"
      @demo="handleDemo"
    />
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import HomeHeroPreview from './HomeHeroPreview.vue';
import HomeLibrarySection from './HomeLibrarySection.vue';
import HomeScenarioSection from './HomeScenarioSection.vue';
import HomeWorkflowSection from './HomeWorkflowSection.vue';
import HomeFooter from './HomeFooter.vue';
import { useAuth } from '@/composables/auth';
import { useViews } from '@/composables/views';
import { useModals } from '@/composables/modals';
import { useHomeMotion } from '@/composables/useHomeMotion';
import { demoLessonProps } from '@/composables/demoLesson';

export default {
  name: 'HomePage',
  components: {
    HomeHeroPreview,
    HomeLibrarySection,
    HomeScenarioSection,
    HomeWorkflowSection,
    HomeFooter
  },
  setup() {
    const { user } = useAuth();
    const { handleViewChange } = useViews();
    const { showLoginModal, handleUpgrade } = useModals();
    const { t, tm, locale } = useI18n();
    const isLoggedIn = computed(() => !!user.value);
    const isEnglish = computed(() => locale.value === 'en');
    const homePage = ref(null);

    useHomeMotion(homePage);

    const tools = computed(() => tm('home.tools.list'));
    const footerTools = computed(() => tools.value.slice(0, 4));

    const stats = computed(() => [
      { value: '99.8%', label: t('home.stats.accuracy') },
      { value: t('home.stats.bilingualValue'), label: t('home.stats.bilingual') },
      { value: '10000+', label: t('home.stats.users') },
      { value: '68575h', label: t('home.stats.hoursTranscribed') },
      { value: '51.87M', label: t('home.stats.charactersGenerated') }
    ]);

    const handleDemo = () => {
      handleViewChange('AudioPlayer', demoLessonProps({
        audioName: t('audioPlayer.demoVideoName'),
        returnView: 'HomePage'
      }));
    };

    const handleShowPremium = () => {
      handleUpgrade();
    };

    const handleStart = () => {
      if (isLoggedIn.value) {
        handleViewChange('Material');
      } else {
        showLoginModal();
      }
    };

    return {
      footerTools,
      handleDemo,
      handleShowPremium,
      handleStart,
      homePage,
      isEnglish,
      isLoggedIn,
      stats
    };
  }
};
</script>

<style>
@import '../../styles/home-main.css';
@import '../../styles/home-mobile.css';
@import '../../styles/home-hero.css';
</style>
